import { Navigate, useSearchParams } from 'react-router'

import { isProduction } from '@/config/config'
import { useCurrentUser } from '@/models/user'
import { CLAIM_ROWS, ClaimOutcome, ClaimStatus, NO_CLAIMS } from '../prototypes/Claims/claimsData'
import { ClaimsList } from '../prototypes/Claims/ClaimsList'
import { ClaimTracker } from '../prototypes/Claims/ClaimTracker'

interface TrackerVariant {
  status: ClaimStatus
  outcome?: ClaimOutcome
  needsYou?: boolean
}

const TRACKERS: Record<string, TrackerVariant | undefined> = {
  [ClaimStatus.IN_REVIEW]: { status: ClaimStatus.IN_REVIEW },
  [ClaimStatus.OPEN_WITH_CARRIER]: { status: ClaimStatus.OPEN_WITH_CARRIER },
  'needs-you': { status: ClaimStatus.OPEN_WITH_CARRIER, needsYou: true },
  'closed-paid': { status: ClaimStatus.CLOSED, outcome: ClaimOutcome.PAID },
  'closed-denied': { status: ClaimStatus.CLOSED, outcome: ClaimOutcome.DENIED },
}

/**
 * One claims screen rendered alone, for the prototype page to embed in a
 * phone-width iframe — `sm:` breakpoints resolve against the iframe's own
 * viewport, which is the only way to show the real mobile layout on a desktop page.
 *
 * Carries the same production staff gate as PrototypeFrame: the route is reachable
 * directly, so without it any signed-in customer could open the frame. PrivateLayout
 * has already resolved the user by the time this renders.
 */
export function ClaimFrame() {
  const [searchParams] = useSearchParams()
  const { user } = useCurrentUser({ enabled: isProduction })
  const screen = searchParams.get('screen') ?? 'list'
  const tracker = TRACKERS[screen]

  if (isProduction && !user?.isCaiAdmin) {
    return <Navigate to="/insure" replace />
  }

  return (
    <div className="p-4">
      {tracker ? (
        <ClaimTracker status={tracker.status} outcome={tracker.outcome} needsYou={tracker.needsYou} />
      ) : (
        <ClaimsList claims={screen === 'list-empty' ? NO_CLAIMS : CLAIM_ROWS} />
      )}
    </div>
  )
}
